import { Link, Navigate, useParams } from 'react-router-dom'
import {
  entityFor,
  findCompany,
  parentFor,
  personLineFor,
  type Listing,
  type Review,
} from '../../content/companies'
import { useLocale, useT } from '../../lib/i18n'
import styles from './Company.module.css'

/**
 * THE COMPANY PAGE — a service listing, presented as a service listing.
 *
 * One address, `/c/:companyId`, resolved from `content/companies.ts` the way `/p/:packId` is
 * resolved from `content/scenarios.ts`. It is reached from ONE row on the CHATS shelf, and it is
 * the only thing that row opens.
 *
 * ⚠ A DESTINATION, NEVER A GATE. Every pack it links to keeps its own address and its own
 * behaviour: `/p/the-argument` opens straight into the booth for anyone who was sent that link,
 * with nothing on this page in the way. Nothing in `PackSurface` reads this file.
 *
 * ── WHY IT LOOKS LIKE THIS ────────────────────────────────────────────────────────────────
 * The page is INFORMATION ARCHITECTURE rather than costume: a name, a strap, listings with a
 * provider, a standing, facts, a rate card, small print, one review, and terms at the foot. That
 * is what makes it read as a listing — not a light theme, not a logo, not a set of borrowed web
 * furniture. The product has no artist (the same judgement that took the drawn doors out on
 * 2026-08-23), so this is built out of the tokens the rest of the surface is built out of, and it
 * earns its genre from what it says and the order it says it in.
 *
 * ⚠ NOTHING ON IT WINKS, and that rule is enforced in `companies.ts`, not here — this file only
 * renders. The one place it could have leaked is the review, which is quoted verbatim and reported
 * with a count, and never framed, softened, introduced, or commented on.
 *
 * ⚠ THE CONTROLS ARE DIEGETIC VERBS, and the reason is the meta-collapse (chat-pack §4): the
 * fiction of this card is a paid bounded conversation, and the product is a paid bounded
 * conversation, so *book five minutes* is both the in-world act and the real one. A PLAY button
 * here would be the only 4th-wall break on the page.
 *
 * ── THE NAME, AND WHERE THE LEGAL FORM IS ALLOWED TO APPEAR (2026-08-26) ─────────────────────
 * The header takes the BRAND ALONE, because that is how a company writes its own name at the top
 * of its own page: a masthead reading *Colloquy Services Ltd.* is a letterhead, not a marque. The legal
 * form and the registered seat appear exactly once, at the foot, on the legal line — which is
 * where a real listing puts them and the only place they are not shouting.
 *
 * Both come from `entityFor`, per locale, so the page is headed by whichever national entity the
 * reader is a customer of. Nothing here knows what the names mean or that they rhyme.
 */

function stars(n: number) {
  return '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n)
}

/**
 * THE REVIEW. Quoted whole, dated, with the seller's replies reported as a count.
 *
 * A listing that showed only its rating would be the site flattering its sellers; a real one
 * shows the worst review someone left and what the seller did about it. Both halves are printed
 * flat — the site does not editorialise about its own sellers, and this page does not either.
 */
function ReviewBlock({ review }: { review: Review }) {
  return (
    <div className={styles.review}>
      <div className={styles.reviewHead}>
        <span className={styles.reviewStars} role="img" aria-label={`${review.stars} out of 5`}>
          {stars(review.stars)}
        </span>
        <span className={styles.reviewWhen}>{review.when}</span>
      </div>
      <p className={styles.reviewBody}>{review.body}</p>
      <p className={styles.reviewResponse}>{review.sellerResponse}</p>
    </div>
  )
}

function ListingBlock({ listing }: { listing: Listing }) {
  return (
    <article className={styles.listing}>
      <h2 className={styles.listingTitle}>{listing.title}</h2>

      {/* Provider and standing on one line, the way a marketplace prints them: who is selling
          this, and what the record says about them. */}
      <p className={styles.provider}>
        <span className={styles.providerName}>{listing.provider}</span>
        <span className={styles.standing}>{listing.standing}</span>
      </p>

      <p className={styles.meta}>{listing.meta.join(' · ')}</p>
      <p className={styles.body}>{listing.body}</p>

      {listing.tiers && (
        <dl className={styles.tiers}>
          {listing.tiers.map((t) => (
            <div key={t.name} className={styles.tier}>
              <dt className={styles.tierHead}>
                <span className={styles.tierName}>{t.name}</span>
                <span className={styles.tierPrice}>{t.price}</span>
              </dt>
              <dd className={styles.tierBody}>{t.body}</dd>
            </div>
          ))}
        </dl>
      )}

      {listing.note && <p className={styles.note}>{listing.note}</p>}

      {/*
        The control. A real link to the pack's own address, so it middle-clicks, long-presses and
        copies like the link it is — which is the same reason a held shelf card is not an anchor.
      */}
      <Link to={listing.href} className={styles.book}>
        {listing.action}
      </Link>

      {listing.review && <ReviewBlock review={listing.review} />}
    </article>
  )
}

export default function Company() {
  const { companyId } = useParams()
  const { locale } = useLocale()
  const t = useT()
  const company = findCompany(companyId)

  if (!company) return <Navigate to="/" replace />

  const entity = entityFor(company, locale)
  const parent = parentFor(company, locale)

  return (
    <div className={styles.page}>
      {/*
        The one piece of chrome, and it is the surface's own vocabulary rather than this page's:
        a bare chevron with the label a reader using their ears hears. Same control, same key, as
        the way out of a thread.
      */}
      <Link to="/" className={styles.back} aria-label={t('shelf.back')}>
        <span aria-hidden="true">‹</span>
      </Link>

      <div className={styles.inner}>
        <header className={styles.header}>
          <h1 className={styles.name}>{entity.brand}</h1>
          <p className={styles.strap}>{company.strap}</p>
        </header>

        {company.listings?.map((l) => (
          <ListingBlock key={l.id} listing={l} />
        ))}

        {/*
          THE ROSTER — the bare genre (founder-ruled 2026-08-27: "literally just the title,
          subtitle, and a list of characters"). Each row is the whole offer: a name, the founder's
          one sentence about them, and the pack's own address. No terms, no legal line, no
          furniture follows it — a roster page ends when the rows do.
        */}
        {company.people?.map((p) => (
          <Link key={p.id} to={p.href} className={styles.personRow}>
            <span className={styles.personName}>{p.name}</span>
            <span className={styles.personLine}>{personLineFor(p, locale)}</span>
          </Link>
        ))}

        {company.terms && (
          <section className={styles.terms}>
            <h2 className={styles.termsHead}>Terms</h2>
            <ul className={styles.termsList}>
              {company.terms.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
        )}

        {/*
          THE LEGAL LINE — the quietest text on the page, and the only place the legal form and
          the registered seat are printed. It is a fact about who you are contracting with, set
          the size a real listing sets it, and it says nothing else. ⚠ MARKETPLACE GENRE ONLY —
          a roster page carries no legal furniture; the suffix still shows on the shelf row.

          ⚠ THE CONNECTIVES ARE ENGLISH ON EVERY LOCALE and that is the page's stated position,
          not an unfinished translation: the whole page is placeholder English (`companies.ts`),
          and only the ENTITY is per-market because being per-market is the content. When the pen
          goes over this page in five languages these two phrases go with it.

          ⚠ THE GROUP LINE IS THE OPTIONAL HALF — `parentFor` returns nothing when the company
          declares no parent, and nothing in the parent's own market. Removing it for good is one
          line in `companies.ts` (`parentLocale`), not an edit here.
        */}
        {company.listings && (
          <p className={styles.legal}>
            {entity.brand} {entity.suffix} · registered in {entity.seat}
            {parent && ` · a subsidiary of ${parent.brand} ${parent.suffix}, ${parent.seat}`}
          </p>
        )}
      </div>
    </div>
  )
}
